import React, { useCallback, useMemo } from 'react'
import { AsyncSelect, Flex } from '@workwhile/ui'
import { useBulkCreateShiftsContextValue } from '../../../context/BulkCreateShiftsProvider'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { FieldPath, useController, useFormContext } from 'react-hook-form'
import { ShiftsTableSchema } from '../../../bulkCreateShiftsSchema'
import { ErrorMessage } from './ErrorMessage'

type CompanyOption = { label: string; value: number }

const fuzzyOptions: FullOptions<CompanyOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export const CompanySelector = ({
  fieldName,
}: {
  fieldName: FieldPath<ShiftsTableSchema>
}) => {
  const { control } = useFormContext<ShiftsTableSchema>()

  const { field, fieldState } = useController({
    control,
    name: fieldName,
  })

  const {
    state: { companies },
  } = useBulkCreateShiftsContextValue()

  const companiesOptions = useMemo(() => {
    return companies?.map((company) => ({
      label: `${company.name} (${company.id})`,
      value: company.id,
    }))
  }, [companies])

  const searcher = useMemo(() => {
    return new Searcher<CompanyOption, FullOptions<CompanyOption>>(
      companiesOptions ?? [],
      fuzzyOptions
    )
  }, [companiesOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<CompanyOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  return (
    <Flex flexDirection="column" gap={1}>
      <AsyncSelect
        block
        {...field}
        isClearable
        isSearchable
        defaultOptions={companiesOptions}
        loadOptions={loadOptions}
        menuPortalTarget={document.body}
        value={
          companiesOptions.find((option) => option.value === field.value) ||
          null
        }
        onChange={(option) => {
          field.onChange(option?.value)
        }}
        styles={{
          container: (base) => ({
            ...base,
            width: '200px',
          }),
        }}
      />
      <ErrorMessage error={fieldState.error?.message} />
    </Flex>
  )
}
